/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useReducer } from "react";
import { useOutletContext, useLocation, useSearchParams } from "react-router-dom";
import { Pencil, Plus, Search, Trash, SearchX, ChevronLeft, ChevronRight } from "lucide-react";
import { isAxiosError } from "axios";
import { axiosErrorToStringError } from "@/utils/axiosErrorToStringError";
import { ScaleLoader } from "react-spinners";
import { Link } from "@/router";
import { useAdminGetForums, useAdminDeleteForum } from "@/services/forumService";
import { useAdminGetForumGroupsSWR } from "@/services/forumGroupService";
import { useAdminGetSkillsSWR } from "@/services/skillService";
import { objectToQueryString } from "@/utils/objectToQueryString";
import { queryStringToObject } from "@/utils/queryStringToObject";


const initialState = {
    errorMessage: "",
    isLoading: false,
    filters: {
        id: "",
        name: "",
        group_id: "",
        skill_id: "",
        username: "",
    },
    forums: null,
    page_size: 10,
    page: 1,
    total: 0
};

const ACTIONS = {
    SET_ERROR_MESSAGE: "setErrorMessage",
    SET_IS_LOADING: "setIsLoading",
    SET_FILTER: "setFilter",
    SET_FILTERS: "setFilters",
    SET_FORUMS: "setForums",
    SET_PAGE_SIZE: "setPageSize",
    SET_PAGE: "setPage",
    SET_TOTAL: "setTotal",
};

const reducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_ERROR_MESSAGE:
            return { ...state, errorMessage: action.payload };
        case ACTIONS.SET_IS_LOADING:
            return { ...state, isLoading: action.payload };
        case ACTIONS.SET_FILTER:
            return { ...state, filters: { ...state.filters, [action.subtype]: action.payload } };
        case ACTIONS.SET_FILTERS:
            return { ...state, filters: { ...state.filters, ...action.payload } };
        case ACTIONS.SET_FORUMS:
            return { ...state, forums: action.payload };
        case ACTIONS.SET_PAGE_SIZE:
            return { ...state, page_size: action.payload }
        case ACTIONS.SET_PAGE:
            return { ...state, page: action.payload }
        case ACTIONS.SET_TOTAL:
            return { ...state, total: action.payload }
        default:
            console.error("Unknown action.type " + action.type);
            throw new Error();
    }
};

export default function Index() {
    const [, setPageTitle] = useOutletContext();
    const [state, dispatch] = useReducer(reducer, initialState);
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const adminGetForums = useAdminGetForums();
    const adminDeleteForum = useAdminDeleteForum();
    const {data: skills} = useAdminGetSkillsSWR();
    const {data: groups} = useAdminGetForumGroupsSWR();
    


    /* On met à jour le titre */
    useEffect(() => {
        setPageTitle("Gestion des forums");

        return () => {
            setPageTitle("Titre")
        }
    }, [setPageTitle]);



    /* On récupère les filtres de recherche si ils sont présents dans l'URL */
    useEffect(() => {
        if (searchParams) {
            dispatch({ type: ACTIONS.SET_FILTERS, payload: queryStringToObject(searchParams.toString()) });
        }
    }, [searchParams])



    /* On met à jour l'URL quand les filtres changent */
    useEffect(() => {
        if(JSON.stringify(state.filters) !== JSON.stringify(searchParams)) {
            setSearchParams(objectToQueryString(state.filters));
        }
    }, [state.filters, setSearchParams, searchParams])



    /* Lorsque la page charge, si les parametres sont vides, on set skip et limit */
    useEffect(() => {
        if (searchParams.size == 0) {
            setSearchParams("limit=10&skip=0")
        }
    }, [])



    /* Lorsque l'on modifie la taille de la page on rafraichit */
    useEffect(() => {
        if(state?.page_size)
            dispatch({ type: ACTIONS.SET_PAGE, payload: 1 })
            dispatch({ type: ACTIONS.SET_FILTER, subtype: "skip", payload: String((state.page - 1) * state.page_size) })
            dispatch({ type: ACTIONS.SET_FILTER, subtype: "limit", payload: String(state.page_size) })
    }, [state.page_size])



    /* Lorsque l'on modifie la page on rafraichit */
    useEffect(() => {
        if(state?.page)
            dispatch({ type: ACTIONS.SET_FILTER, subtype: "skip", payload: String((state.page - 1) * state.page_size) })
    }, [state.page])



    /* Lorsque les états skip et limit ont changé, on rafraichit */
    useEffect(() => {
        if (state.total > 0)
            searchHandler()
    }, [state.filters.skip, state.filters.limit])



    /* Handler de la recherche */
    const searchHandler = async () => {
        const filters = state.filters;
        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
        dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });

        try {
            const [forums, total] = await adminGetForums(filters).withTotal();
            dispatch({ type: ACTIONS.SET_FORUMS, payload: forums });
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
            dispatch({ type: ACTIONS.SET_TOTAL, payload: total });
        } catch (error) {
            console.error("admin/forums/index::searchHandler", error);
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }
    };



    /* Gestion du bouton de reset des filtres */
    const clearFiltersHandler = () => {
        dispatch({ type: ACTIONS.SET_FILTERS, payload: initialState.filters });
    }



    /* Handler de la suppression d'un forum */
    const deleteForumHandler = async (forum) => {
        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
        dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });

        try {
            await adminDeleteForum(forum.id);
            searchHandler();
        } catch (error) {
            console.error("admin/forums/index::deleteForumHandler::error", error);
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }
    };



    return (
        <>
            {/* Filtres de recherche */}

            <h2 className="text-secondary text-lg flex-none content-center">Filtres de recherche</h2>
            <div className="flex flex-wrap w-full justify-start gap-4">
                <div className="flex flex-col justify-between h-12">
                    <label className="text-primary text-sm" htmlFor="searchID">
                        ID
                    </label>
                    <input
                        type="text"
                        id="searchID"
                        className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
                        placeholder="ID"
                        onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "id", payload: e.target.value })}
                        value={state.filters.id}
                    />
                </div>
                <div className="flex flex-col justify-between">
                    <label className="text-primary text-sm" htmlFor="searchName">
                        Nom
                    </label>
                    <input
                        type="text"
                        id="searchName"
                        className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
                        placeholder="Nom"
                        onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "name", payload: e.target.value })}
                        value={state.filters.name}
                    />
                </div>
                <div className="flex flex-col justify-between">
                    <label className="text-primary text-sm" htmlFor="searchUsername">
                        Nom d&apos;utilisateur
                    </label>
                    <input
                        type="text"
                        id="searchUsername"
                        className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
                        placeholder="Nom d&apos;utilisateur"
                        onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "username", payload: e.target.value })}
                        value={state.filters.username}
                    />
                </div>
                <div className="flex flex-col justify-between">
                    <label className="text-primary text-sm" htmlFor="searchGroup">
                        Groupe
                    </label>
                    <select
                        id="searchGroup"
                        className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
                        placeholder="Groupe"
                        onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "group_id", payload: e.target.value })}
                        value={state.filters.group_id}
                    >
                        <option value="">-</option>
                        {groups?.map((group, i) => {
                            return (
                                <option key={i} value={group.id}>
                                    {group.name}
                                </option>
                            );
                        })}
                    </select>
                </div>
                <div className="flex flex-col justify-between">
                    <label className="text-primary text-sm" htmlFor="searchSkill">
                        Métier
                    </label>
                    <select
                        id="searchSkill"
                        className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
                        placeholder="Métier"
                        onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "skill_id", payload: e.target.value })}
                        value={state.filters.skill_id}
                    >
                        <option value="">-</option>
                        {skills?.map((skill, i) => {
                            return (
                                <option key={i} value={skill.id}>
                                    {skill.code} - {skill.name}
                                </option>
                            );
                        })}
                    </select>
                </div>
                <div className="flex flex-col justify-end">
                    <button 
                        id="searchButton" name="searchButton"
                        className="pb-1" onClick={() => {dispatch({ type: ACTIONS.SET_PAGE, payload: 1 }); searchHandler()}}
                        title="Rechercher">
                        <Search size={32} className="text-primary hover:text-blue-600" />
                    </button>
                </div>
                <div className="flex flex-col justify-end">
                    <button 
                        id="clearFiltersButton" name="clearFiltersButton"
                        className="pb-1" onClick={() => clearFiltersHandler()}
                        title="Nettoyer la recherche">
                        <SearchX size={20} className="text-primary hover:text-red-600" />
                    </button>
                </div>
            </div>

            {/* Affichage de la liste des forums */}

            <div className="flex flex-wrap w-full h-14 justify-between mb-3 ">
                <div className="flex content-center items-center gap-2">
                    <h2 className="text-secondary text-lg flex-none content-center">Liste des forums</h2>
                    <select className="border-1 border-gray-400 py-1 text-sm text-secondary h-10"
                        onChange={(e) => dispatch({ type: ACTIONS.SET_PAGE_SIZE, payload: e.target.value })} 
                        value={state.page_size}
                    >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                </div>
                <div className="flex flex-wrap content-center">
                    {state.errorMessage && (
                        <span className="text-red-700 flex-none content-center mr-4">{state.errorMessage}</span>
                    )}
                    {state.isLoading ? <ScaleLoader height={20} color={"#696969"} cssOverride={{alignSelf: "center"}} /> : null}
                    <Link
                        to="/admin/forums/add"
                        state={{ from: location}}
                        className="ml-4 border rounded-lg h-10 w-10 border-secondary border-dashed hover:bg-blue-100 content-center"
                    >
                        <Plus className="mx-auto" />
                    </Link>
                </div>
            </div>
            <table className="table-auto w-full border border-separate rounded-xl border-blue-500 border-spacing-0">
                <thead className="bg-blue-200">
                    <tr className="h-10">
                        <th className="px-4 py-2 text-left rounded-tl-xl">Nom</th>
                        <th className="px-4 py-2 text-center">Description</th>
                        <th className="px-4 py-2 text-center">Sujets</th>
                        <th className="px-4 py-2 text-center">Groupes</th>
                        <th className="px-4 py-2 text-center rounded-tr-xl">Paramètres</th>
                    </tr>
                </thead>
                <tbody>
                    {state.forums?.length > 0 
                        ? ( state.forums.map((forum, i) => {
                            return (
                                <tr
                                    key={forum.id}
                                    className={` ${i % 2 === 0 && "bg-blue-100"} "hover:bg-sky-200" `} >
                                        <th className={`text-left px-1 flex-auto flex-wrap flex flex-col gap-1 ${i === state.forums.length - 1 && "rounded-bl-xl"}`}>
                                            <div className="text-secondary text-xs flex items-center gap-2">
                                                <span className="content-center">ID:{forum.id}</span>
                                                <button
                                                    onDoubleClick={() => deleteForumHandler(forum)}
                                                    title="Double-cliquez pour supprimer le forum"
                                                >
                                                    <Trash size={10} className="text-red-500 hover:text-red-700" />
                                                </button>
                                            </div>
                                            <div className="text-primary text-base flex items-center">
                                                <Link to={"/admin/forums/" + forum.id} state={{ from: location}}>
                                                    <Pencil size={12} className="flex content-center mr-2" />
                                                </Link>

                                                <span className="flex content-center text-sm">
                                                    {forum.name}
                                                </span>
                                            </div>
                                        </th>
                                        <td className="text-left text-secondary text-xs w-[30%]">
                                            <span>{forum.description}</span>
                                        </td>
                                        <td className="text-center text-secondary text-xs">
                                            <span>{forum.topic_count}</span>
                                        </td>
                                        <td className="px-4 py-2 text-left flex-auto">
                                            <div className="flex flex-wrap w-full gap-1 justify-center">
                                                {forum.groups?.map((group, i) => {
                                                    const bgColor = group.color ? `bg-${group.color}-500` : "bg-secondary";
                                                    const borderColor = group.color ? `border-${group.color}-500` : "bg-secondary";
                                                    const textColor = group.color ? `text-white` : "text-secondary";
                                                    return (
                                                        <span key={i} 
                                                            className={`text-nowrap border border-1 rounded-md px-2 py-1 text-xs ${bgColor} ${borderColor} ${textColor}`}
                                                        >
                                                            <Link to={`/admin/forum_groups/${group.id}`} className="underline">
                                                                {group.name}
                                                            </Link>
                                                        </span>
                                                    )
                                                })}
                                            </div>
                                        </td>
                                        <td className="text-left text-xs flex flex-col flex-wrap gap-1">
                                            {
                                                forum.skill_id ?
                                                <span>Métier : <Link to={`/admin/skills/${forum.skill_id}`} className="underline">
                                                    {skills?.find(skill => skill.id === forum.skill_id)?.code}
                                                </Link></span>
                                                : ""
                                            }

                                            {
                                                forum?.specific_rights_count > 0 ?
                                                <span>Droits : <Link to={`/admin/forums/${forum.id}/rights`} className="underline">
                                                    {forum?.specific_rights_count || 0}
                                                </Link></span>
                                                : ""
                                            }
                                            
                                        </td>
                                </tr>
                            );
                        })) : state.forums ? (
                            <tr>
                                <td colSpan={3} className="text-center text-secondary py-3">
                                    Aucun forum trouvé
                                </td>
                            </tr>
                        ) : (
                            <tr>
                                <td colSpan={3} className="text-center text-secondary py-3">
                                    Lancez la recherche pour afficher les forums
                                </td>
                            </tr>
                        )}
                </tbody>
            </table>
            { state.total > 0 &&
                <div className="w-full flex flex-row gap-5 justify-end mt-4">
                                      
                    <div className="flex flex-row gap-2 items-center border-collapse">
                        
                        <button 
                            className="text-base border border-gray-400 px-2 rounded-s-xl hover:bg-gray-200 text-secondary border-collapse disabled:cursor-not-allowed"
                            disabled={state.page <= 1 || state.isLoading}
                            onClick={(e) => {e.preventDefault(); dispatch({ type: ACTIONS.SET_PAGE, payload: state.page - 1 })}}
                        >
                            <ChevronLeft size={24} />
                        </button>

                        <span className="text-secondary text-base border border-gray-400 px-2 border-collapse">
                            {state.page} / {Math.ceil(state.total / state.page_size)}
                        </span>

                        <button 
                            className="text-base border border-gray-400 px-2 rounded-e-xl hover:bg-gray-200 text-secondary border-collapse disabled:cursor-not-allowed"
                            disabled={state.page >= Math.ceil(state.total / state.page_size) || state.isLoading}
                            onClick={(e) => {e.preventDefault(); dispatch({ type: ACTIONS.SET_PAGE, payload: state.page + 1 })}}
                        >
                            <ChevronRight size={24} />
                        </button>

                    </div>
                </div>
            }
        </>
    );
}